import HttpService from './HttpService';

const API_URL = '/config';

/**
 * Built-in fallback for the scene-constants vocabulary. Mirrors the
 * server's canonical copy in `api/config.py`. Used for unit tests, for
 * the first render before the fetch resolves, and as a safety net if
 * the endpoint is briefly unavailable. Renames must happen in BOTH
 * places — there's a paired backend test that pins the wire shape.
 */
export const DEFAULT_SCENE_CONSTANTS = Object.freeze({
    edge_directions: Object.freeze({
        VERTICAL: 'vertical',
        HORIZONTAL: 'horizontal',
        SLOPED: 'sloped',
    }),
    edge_labels: Object.freeze({
        TALL: 'Tall',
        SHORT: 'Short',
        SIDE: 'Side',
        TOP: 'Top',
        FLOOR: 'Floor',
        SLOPE: 'Slope',
    }),
    unit_systems: Object.freeze({
        METERS: 'meters',
        FEET: 'feet',
        INCHES: 'inches',
    }),
});

/**
 * Built-in fallback for the per-utility-type usage-unit vocabulary.
 * Mirrors the server's canonical copy in `api/config.py` (USAGE_UNITS).
 * Used for the first render before the fetch resolves, for unit tests,
 * and as a safety net if the endpoint is briefly unavailable. Renames /
 * additions must happen in BOTH places — a paired backend test pins the
 * wire shape. The first unit in each list is the default the form
 * pre-fills for that type.
 */
export const DEFAULT_USAGE_UNITS = Object.freeze({
    electric: Object.freeze(['kWh']),
    gas: Object.freeze(['therms', 'CCF', 'MCF']),
    water: Object.freeze(['gal', 'kgal', 'CCF']),
    internet: Object.freeze(['GB', 'TB']),
    sewer: Object.freeze(['gal', 'kgal']),
    cable: Object.freeze([]),
    trash: Object.freeze([]),
    other: Object.freeze([]),
});

/**
 * True only when `value` is a well-formed usage-units map: a plain object with
 * a string-array for every expected utility type. Guards the consumer contract
 * — `UtilityDetails` does `unitOptions.map(...)`, so a truthy-but-malformed
 * payload (e.g. `{ electric: 'kWh' }`) would throw there rather than falling
 * back. Falsy checks alone don't catch that; validate the shape.
 */
const isUsageUnitsMap = (value) =>
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(DEFAULT_USAGE_UNITS).every(
        (type) =>
            Array.isArray(value[type]) &&
            value[type].every((unit) => typeof unit === 'string'),
    );

const ConfigService = {
    /**
     * Fetch the floor-plan scene vocabulary from the backend.
     *
     * Resolves to the same shape as `DEFAULT_SCENE_CONSTANTS`. On
     * network / server failure, resolves to the fallback instead of
     * rejecting — callers can render with the local mirror while
     * displaying a soft warning if they care to.
     */
    getSceneConstants: async () => {
        try {
            const response = await HttpService.get(`${API_URL}/scene-constants`);
            return response.data;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('ConfigService.getSceneConstants failed; using built-in fallback', error);
            }
            return DEFAULT_SCENE_CONSTANTS;
        }
    },

    /**
     * Fetch the per-utility-type usage-unit vocabulary from the backend.
     *
     * Resolves to the map object (same shape as `DEFAULT_USAGE_UNITS`),
     * unwrapping the endpoint's `{ usage_units: {...} }` envelope. On
     * network / server failure, resolves to the local fallback instead of
     * rejecting so the bill form always has units to offer.
     */
    getUsageUnits: async () => {
        try {
            const response = await HttpService.get(`${API_URL}/usage-units`);
            // Optional-chain the top-level response (a null/undefined response
            // would otherwise throw) AND validate the shape: a truthy-but-
            // malformed payload must fall back rather than reach the form's
            // `.map()`.
            const usageUnits = response?.data?.usage_units;
            return isUsageUnitsMap(usageUnits) ? usageUnits : DEFAULT_USAGE_UNITS;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('ConfigService.getUsageUnits failed; using built-in fallback', error);
            }
            return DEFAULT_USAGE_UNITS;
        }
    },
};

export default ConfigService;
